import MDBox from 'components/MDBox'
import MDButton from 'components/MDButton'
import MDInput from 'components/MDInput'
import MDModal from 'components/MDModal'
import MDTypography from 'components/MDTypography'
import { useAppServices } from 'hook/services'
import React, { useState, useEffect, useMemo } from 'react'
import * as yup from 'yup'
import { useFormik } from 'formik'
import { useLocation } from 'react-router-dom'
import { TextareaAutosize, Autocomplete, TextField } from '@mui/material'

const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  border: '2px  #000',
  boxShadow: 24,
  p: 4,
}
const DocsValidate = yup.object({
  name: yup.string().required().label('Name'),
})
function Category({ open, onClose, category, handleRefresh }) {
  const { state } = useLocation()
  const { view_category } = state
  const [processing, setProcessing] = useState(false)
  const [paths, setPaths] = useState([])
  const [selectedPath, setSelectedPath] = useState(null)
  const [searchInput, setSearchInput] = useState('')
  const AppService = useAppServices()

  // Fetch paths when component mounts or modal opens
  const getToolTip = async () => {
    const { response } = await AppService.tooltip.get({
      toaster: false,
    })
    if (response) {
      setPaths(response.pages)
      // Pre-fill selected path when editing existing category
      if (category?.paths && category.paths.length > 0) {
        const existingPath = category.paths[0]
        const matchedPath = response.pages.find(path =>
          path.path === existingPath.path || path.name === existingPath.name
        )
        if (matchedPath) {
          setSelectedPath({
            label: matchedPath.name,
            value: matchedPath.path,
            ...matchedPath
          })
        }
      }
    } else {
      setPaths([])
    }
  }

  useEffect(() => {
    if (open) {
      getToolTip()
    }
  }, [open, category])

  // Transform paths for Autocomplete
  const pathOptions = useMemo(() => {
    return paths?.map(path => ({
      label: path.name,
      value: path.path,
      ...path
    })) || []
  }, [paths])

  // Filter options based on search input
  const filteredOptions = useMemo(() => {
    if (!searchInput) return pathOptions
    return pathOptions.filter(option =>
      option.label.toLowerCase().includes(searchInput.toLowerCase()) ||
      option.value.toLowerCase().includes(searchInput.toLowerCase())
    )
  }, [pathOptions, searchInput])

  const handleSubmit = async (e) => {
    setProcessing(true)
    e.preventDefault()

    // Prepare the paths array for payload
    const pathsPayload = selectedPath ? [
      {
        name: selectedPath.label,
        path: selectedPath.value
      }
    ] : []

    const payload = {
      name: e.target.name.value,
      category: view_category._id,
      type: 'super-admin',
      paths: pathsPayload, // Add the paths array to payload
    }

    if (category) {
      payload._id = category._id
      payload.type = category?.type || 'super-admin'
    }

    console.log('Final payload:', payload) // For debugging

    const { response } = await AppService.docs_sub_category[category?._id ? 'update' : 'create']({
      payload,
      toaster: true,
    })

    setProcessing(false)
    if (response) {
      onClose()
      handleRefresh()
    }
  }

  const initstate = {
    name: category?.name || '',
  }

  const formik = useFormik({
    initialValues: { ...initstate },
    onSubmit: handleSubmit,
    validationSchema: DocsValidate,
  })

  // Reset form when modal closes
  useEffect(() => {
    if (!open) {
      setSelectedPath(null)
      setSearchInput('')
    }
  }, [open])

  return (
    <MDBox>
      <MDModal
        open={open}
        onClose={onClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <MDBox role="form" component="form" onSubmit={handleSubmit} sx={style}>
          <MDTypography variant="h6">
            {category?._id ? 'Edit' : 'Create'} Docs Sub Category
          </MDTypography>

          <MDBox my={2}>
            <MDInput
              type="text"
              label="Name"
              name="name"
              onChange={formik.handleChange}
              inputProps={{ onFocus: formik.handleBlur }}
              value={formik.values.name}
              error={formik.touched.name && formik.errors.name}
              helperText={formik.touched.name && formik.errors.name ? formik.errors.name : ''}
              success={formik.touched.name && !formik.errors.name}
              fullWidth
            />
          </MDBox>

          {/* Path Selection with Search */}
          <MDBox my={2}>
            <MDTypography variant="button" sx={{ mb: 1, display: 'block' }}>
              Select Path
            </MDTypography>
            <Autocomplete
              options={filteredOptions}
              value={selectedPath}
              onChange={(event, newValue) => {
                setSelectedPath(newValue)
              }}
              inputValue={searchInput}
              onInputChange={(event, newInputValue) => {
                setSearchInput(newInputValue)
              }}
              getOptionLabel={(option) => option.label || ''}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search for a path..."
                  variant="outlined"
                  fullWidth
                  error={false}
                />
              )}
              renderOption={(props, option) => (
                <li {...props}>
                  <MDBox>
                    <MDTypography variant="body2" fontWeight="bold">
                      {option.label}
                    </MDTypography>
                    <MDTypography variant="caption" color="textSecondary">
                      {option.value}
                    </MDTypography>
                  </MDBox>
                </li>
              )}
              isOptionEqualToValue={(option, value) => option.value === value.value}
              noOptionsText="No paths found"
            />
          </MDBox>

          <MDButton
            variant="gradient"
            color="info"
            type="submit"
            className="brand-card-background"
            loading={processing}
            disabled={processing || !formik.isValid}
            sx={{ mt: 4, mb: 1 }}
            fullWidth
          >
            {category?._id ? 'Update' : 'Add'}
          </MDButton>
        </MDBox>
      </MDModal>
    </MDBox>
  )
}

export default Category